import React, { useState, useEffect } from 'react';
import Udesk from 'Udesk';
import Page from 'Component/common/udesk-qa-page-layout';
import { Button, Card, Spin, Modal, FormBuilder, Popconfirm, Icon, Empty, Tabs } from 'udesk-ui';
import './style.scss';
import { IntelligentPartnerTagGroupFoundResponse } from 'src/api/types';
import {
    getIntelligentPartnerTagGroups,
    postIntelligentPartnerTagGroups,
} from 'src/api/intelligentPartnerTagGroups';
import {
    deleteIntelligentPartnerTagGroupsById,
    putIntelligentPartnerTagGroupsById,
} from 'src/api/intelligentPartnerTagGroups/{id}';
import { AddTag } from './components/AddTag';
import { Tag } from './components/Tag';
import styled from 'styled-components';
import UdeskLocales from 'UdeskLocales';

const { TabPane } = Tabs;

const Div = styled.div`
    overflow-y: auto;
    flex: 1;
    .udesk-qa-ui-card-head-title {
        flex: unset;
    }
    .udesk-qa-ui-card-extra {
        flex: 1;
        .customer-tag-groups-toolbar {
            display: flex;
            justify-content: space-between;
        }
    }
`;

const Template = React.memo((props: any) => {
    const [customerTagGroups, setCustomerTagGroups] = useState<
        IntelligentPartnerTagGroupFoundResponse[]
    >([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [visible, setVisible] = useState<boolean>(false);
    const [currentGroup, setCurrentGroup] = useState<IntelligentPartnerTagGroupFoundResponse>({});
    const [activeTab, setActiveTab] = useState<string>('role');
    const [form] = FormBuilder.useForm();

    const queryCustomerTagGroups = async (tagType = 0) => {
        setLoading(true);
        try {
            const { data = [] } = await getIntelligentPartnerTagGroups(
                tagType === 1 ? { params: { tagType } } : {}
            );
            setCustomerTagGroups(data);
            setLoading(false);
        } catch {
            setLoading(false);
        }
    };

    useEffect(() => {
        queryCustomerTagGroups(activeTab === 'role' ? 0 : 1);
    }, [activeTab]);

    const openModal = (group) => {
        setVisible(true);
        setCurrentGroup(group);
        form.setFieldsValue(group);
    };

    const closeModal = () => {
        setVisible(false);
        form.resetFields();
    };

    const handleTabChange = (key: string) => {
        setActiveTab(key);
    };

    const TagContent = () => (
        <Div>
            <Spin spinning={loading} delay={200}>
                {customerTagGroups.map((item) => {
                    return (
                        <Card
                            key={item.id}
                            type="inner"
                            title={item.groupName}
                            extra={
                                <div className="customer-tag-groups-toolbar">
                                    <Button
                                        icon={<Icon type="EditTwoTone" antdIcon={true} />}
                                        size="small"
                                        tooltip={
                                            UdeskLocales['current'].pages.coach
                                                .customerTagsManagement.index.edit
                                        }
                                        onClick={() => {
                                            openModal(item);
                                        }}
                                    />
                                    <Popconfirm
                                        title={
                                            UdeskLocales['current'].pages.coach
                                                .customerTagsManagement.index
                                                .afterDeletionItCannotBeRestoredAreYouSureYouWantToDeleteThisLabelGroup
                                        }
                                        onConfirm={() => {
                                            deleteIntelligentPartnerTagGroupsById({
                                                segments: {
                                                    id: item.id!,
                                                },
                                            }).then(() =>
                                                queryCustomerTagGroups(activeTab === 'role' ? 0 : 1)
                                            );
                                        }}
                                        okText={
                                            UdeskLocales['current'].pages.coach
                                                .customerTagsManagement.index.delete
                                        }
                                        cancelText={
                                            UdeskLocales['current'].pages.coach
                                                .customerTagsManagement.index.cancel
                                        }
                                    >
                                        <Button type="link" danger size="small">
                                            {
                                                UdeskLocales['current'].pages.coach
                                                    .customerTagsManagement.index.delete
                                            }
                                        </Button>
                                    </Popconfirm>
                                </div>
                            }
                        >
                            {(item.tags || []).map((tag) => (
                                <Tag
                                    key={tag.id}
                                    {...tag}
                                    onSubmit={() =>
                                        queryCustomerTagGroups(activeTab === 'role' ? 0 : 1)
                                    }
                                >
                                    {tag.tagName}
                                </Tag>
                            ))}
                            <AddTag
                                onSubmit={() =>
                                    queryCustomerTagGroups(activeTab === 'role' ? 0 : 1)
                                }
                                groupId={item.id}
                                tagType={activeTab === 'role' ? 0 : 1}
                            />
                        </Card>
                    );
                })}
                {!customerTagGroups.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
            </Spin>
        </Div>
    );

    return (
        <Page
            pageBodyClassName="udesk-coach-web-customer-tags-management-index"
            title={UdeskLocales['current'].pages.coach.customerTagsManagement.index.labelManagement}
            padding={true}
            extra={[
                <Button
                    onClick={() =>
                        openModal({ groupName: '', tagType: activeTab === 'role' ? 0 : 1 })
                    }
                    type="primary"
                >
                    {UdeskLocales['current'].pages.coach.customerTagsManagement.index.newGroup}
                </Button>,
            ]}
        >
            <Tabs activeKey={activeTab} onChange={handleTabChange}>
                <TabPane tab="角色标签" key="role">
                    <TagContent />
                </TabPane>
                <TabPane tab="课程标签" key="course">
                    <TagContent />
                </TabPane>
            </Tabs>

            <Modal
                visible={visible}
                onCancel={closeModal}
                title={
                    currentGroup.id
                        ? UdeskLocales['current'].pages.coach.customerTagsManagement.index.editGroup
                        : UdeskLocales['current'].pages.coach.customerTagsManagement.index.newGroup
                }
                onOk={() => {
                    form.validateFields()
                        .then((values) => {
                            const data = {
                                ...values,
                                tagType: activeTab === 'role' ? 0 : 1,
                            };

                            if (!currentGroup.id) {
                                return postIntelligentPartnerTagGroups(data);
                            } else {
                                return putIntelligentPartnerTagGroupsById(
                                    {
                                        ...currentGroup,
                                        ...data,
                                    },
                                    {
                                        segments: {
                                            id: currentGroup.id,
                                        },
                                    }
                                );
                            }
                        })
                        .then(() => {
                            closeModal();
                            queryCustomerTagGroups(activeTab === 'role' ? 0 : 1);
                        });
                }}
            >
                <FormBuilder
                    fields={[
                        {
                            type: 'Input',
                            name: 'groupName',
                            label: UdeskLocales['current'].pages.coach.customerTagsManagement.index
                                .labelGroupName,
                            rules: [{ required: true }],
                            props: {
                                placeholder: UdeskLocales['current'].labels.pleaseEnter,
                            },
                        },
                    ]}
                    form={form}
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 12 }}
                    footerRender={false}
                />
            </Modal>
        </Page>
    );
});

class Component extends React.Component {
    render() {
        return <Template {...this.props} />;
    }
}
export default Udesk.react.udeskify(Component);
